import { useEffect, useRef, useCallback, useState } from 'react';
import { useRealTime } from '../context/RealTimeContext';

/**
 * Hook intelligent pour rafraîchir les données via WebSocket avec polling de fallback
 * 
 * @param {Function} fetchFunction - Fonction pour récupérer les données
 * @param {Object} options - Options de configuration
 * @param {string[]} options.wsEvents - Types d'événements WebSocket à écouter
 * @param {number} options.fallbackInterval - Intervalle de polling fallback en ms (défaut: 30000)
 * @param {boolean} options.enabled - Active/désactive le refresh (défaut: true)
 * @param {boolean} options.refreshOnMount - Rafraîchir au montage (défaut: true)
 * @param {boolean} options.refreshOnFocus - Rafraîchir au focus de la fenêtre (défaut: true)
 */
export function useSmartRefresh(fetchFunction, options = {}) {
  const {
    wsEvents = [],
    fallbackInterval = 5000, // 5 secondes par défaut (fallback uniquement)
    enabled = true,
    refreshOnMount = true,
    refreshOnFocus = true
  } = options;

  const { isConnected, subscribe } = useRealTime();
  const savedCallback = useRef(fetchFunction);
  const intervalRef = useRef(null);
  const lastRefreshRef = useRef(0);
  const [refreshCount, setRefreshCount] = useState(0);

  // Temps minimum entre deux rafraîchissements (évite les appels trop fréquents)
  const MIN_REFRESH_INTERVAL = 500; // 500ms

  // Mettre à jour la référence de la fonction
  useEffect(() => {
    savedCallback.current = fetchFunction;
  }, [fetchFunction]);

  // Fonction de rafraîchissement avec debounce
  const refresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL) {
      return; // Trop tôt pour rafraîchir
    }
    lastRefreshRef.current = now;
    
    if (savedCallback.current) {
      savedCallback.current();
      setRefreshCount(c => c + 1);
    }
  }, []);

  // Rafraîchir au montage
  useEffect(() => {
    if (enabled && refreshOnMount) {
      refresh();
    }
  }, [enabled, refreshOnMount, refresh]);

  // S'abonner aux événements WebSocket
  useEffect(() => {
    if (!enabled || wsEvents.length === 0) return;

    const unsubscribes = wsEvents.map(eventType => {
      return subscribe(eventType, () => {
        // Rafraîchir quand un événement WebSocket arrive
        refresh();
      });
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [enabled, wsEvents, subscribe, refresh]);

  // Polling de fallback - intervalle plus long quand WebSocket est connecté
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Ajuster l'intervalle selon la connexion WebSocket
    const actualInterval = isConnected 
      ? fallbackInterval  // WebSocket connecté: polling lent comme fallback
      : Math.min(fallbackInterval, 5000); // WebSocket déconnecté: polling plus fréquent (5s)

    intervalRef.current = setInterval(refresh, actualInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isConnected, fallbackInterval, refresh]);

  // Rafraîchir au focus de la fenêtre
  useEffect(() => {
    if (!enabled || !refreshOnFocus) return;

    const handleFocus = () => {
      // Rafraîchir seulement si la dernière maj date de plus de 5 secondes
      if (Date.now() - lastRefreshRef.current > 5000) {
        refresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [enabled, refreshOnFocus, refresh]);

  return { 
    refresh, 
    isConnected, 
    refreshCount 
  };
}

/**
 * Événements WebSocket disponibles
 */
export const WS_EVENTS = {
  // Véhicules
  VEHICLE_UPDATED: 'vehicle_updated',
  VEHICLE_CREATED: 'vehicle_created',
  VEHICLE_DELETED: 'vehicle_deleted',
  
  // Employés
  EMPLOYEE_UPDATED: 'employee_updated',
  EMPLOYEE_CREATED: 'employee_created',
  EMPLOYEE_DELETED: 'employee_deleted',
  
  // Tâches
  TASK_UPDATED: 'task_updated',
  TASK_CREATED: 'task_created',
  TASK_DELETED: 'task_deleted',
  
  // Incidents
  INCIDENT_UPDATED: 'incident_updated',
  INCIDENT_CREATED: 'incident_created',
  
  // Planning
  SCHEDULE_UPDATED: 'schedule_updated',
  SCHEDULE_CREATED: 'schedule_created',
  SCHEDULE_DELETED: 'schedule_deleted',
  
  // Messages
  NEW_MESSAGE: 'new_message',
  
  // Utilisateurs
  USER_UPDATED: 'user_updated',
  USER_CREATED: 'user_created',
  USER_DELETED: 'user_deleted',
  
  // Dashboard
  DASHBOARD_UPDATE: 'dashboard_update',
  
  // Data management
  DATA_RESTORED: 'data_restored',
  DATA_DELETED: 'data_deleted'
};

export default useSmartRefresh;
